import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { FormikErrors, FormikTouched, useFormik } from 'formik'
import { colors } from '../../../../theme'
import { GetSubscriptionPlansQuery } from '../../../../graphql/generated'
import { Loader } from '../../../../components/Loader'
import { FormValues, NOTSET } from './registrationFormTypes'

/**
 * Helper component for rendering a price plan picker.
 *
 * It is expected to be rendered as part of a MUI form,
 * and inside a Formik context.
 *
 * When rendering this component, we expect there to be a plan name
 * in the current page's URL query parameters.
 * - If the URL contains a valid plan (expected case)
 *   we render a static text element displaying that plan.
 * - If the plan is not in the URL params, or does not match a valid plan,
 *   we display a selector allowing the user to pick from the plans we offer.
 */
export function PricePlanField(params: {
  /**
   * Name of the plan field in Formik. Used when setting/touching the plan field.
   *
   * Also used as the `name` attribute for the underlying <input> element.
   */
  formikFieldName: keyof FormValues
  /** Label text for the plan, for both alternatives of select/static text element */
  label: string
  values: FormValues
  touched: FormikTouched<FormValues>
  errors: FormikErrors<FormValues>
  subscriptionPlanLoading: boolean
  subscriptionPlanData?: GetSubscriptionPlansQuery
  /** Callback for handling changes in plan selection */
  handleChange: ReturnType<typeof useFormik<FormValues>>['handleChange']
  setFieldValue: ReturnType<typeof useFormik<FormValues>>['setFieldValue']
  setFieldTouched: ReturnType<typeof useFormik<FormValues>>['setFieldTouched']
}): React.JSX.Element {
  const {
    formikFieldName,
    label,
    values,
    touched,
    errors,
    subscriptionPlanLoading,
    subscriptionPlanData,
    handleChange,
    setFieldValue,
    setFieldTouched,
  } = params

  // Track if the URL plan is valid, as we want to render different
  // components depending on this.
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const qpPlan: string | null = queryParams.get('plan')
  const [validUrlPlan, setValidUrlPlan] = useState(false)

  useEffect(() => {
    if (
      qpPlan &&
      !subscriptionPlanLoading &&
      subscriptionPlanData?.getSubscriptionPlans.length
    ) {
      const lowecaseplan = qpPlan.toLowerCase()
      const matchingPlan = subscriptionPlanData.getSubscriptionPlans.find(
        (plan) => {
          return lowecaseplan === plan.name.toLowerCase()
        },
      )
      // If the query param plan matched a valid plan, use that,
      // otherwise use the bad plan name -- it will be displayed as
      // an erroneous option (see the useEffect and Select element below).
      if (matchingPlan) {
        setFieldValue(formikFieldName, matchingPlan.name)
      } else {
        setFieldValue(formikFieldName, qpPlan)
      }
      setValidUrlPlan(!!matchingPlan)
    } else {
      // The url plan is not valid, or we could not validate it.
      // We will allow the user to choose a plan from a list.
      setValidUrlPlan(false)
    }
  }, [
    formikFieldName,
    qpPlan,
    setFieldValue,
    subscriptionPlanData?.getSubscriptionPlans,
    subscriptionPlanLoading,
  ])

  // we want the form to display an error if the plan passed in the query params does not match any proper plan name
  // if we call setFieldTouched in the above useEffect then the validation is done before the value is set (even if we call the setFieldTouched after setFieldValue)
  // so we call it in its own useEffect
  useEffect(() => {
    if (
      values.subscriptionPlanName !== NOTSET &&
      subscriptionPlanData?.getSubscriptionPlans
    ) {
      setFieldTouched(formikFieldName)
    }
  }, [
    formikFieldName,
    setFieldTouched,
    subscriptionPlanData?.getSubscriptionPlans,
    values.subscriptionPlanName,
  ])

  return (
    <FormControl fullWidth variant='standard'>
      {validUrlPlan ? (
        <TextField
          data-testid='subscription-plan-static'
          name={formikFieldName}
          label={label}
          type='text'
          disabled
          value={values.subscriptionPlanName}
          variant='standard'
          slotProps={{
            inputLabel: {
              shrink: true,
              required: true,
              error:
                !!touched.subscriptionPlanName && !!errors.subscriptionPlanName,
            },
            input: {
              readOnly: true,
            },
          }}
        />
      ) : (
        <>
          <InputLabel
            id='subscription-plan-label'
            required
            error={
              !!touched.subscriptionPlanName && !!errors.subscriptionPlanName
            }
          >
            {label}
          </InputLabel>
          <Select
            name={formikFieldName}
            id='subscription-plan-select'
            labelId='subscription-plan-label'
            onChange={handleChange}
            value={values.subscriptionPlanName}
            displayEmpty
            renderValue={
              values.subscriptionPlanName !== NOTSET ? undefined : () => 'Välj'
            }
            error={
              !!touched.subscriptionPlanName && !!errors.subscriptionPlanName
            }
          >
            {subscriptionPlanLoading && (
              <MenuItem value={NOTSET} key={NOTSET}>
                <Loader size={20} borderSize={4} />
                <span>&nbsp;Laddar</span>
              </MenuItem>
            )}
            {subscriptionPlanData?.getSubscriptionPlans.map((plan) => {
              return (
                <MenuItem value={plan.name} key={plan.name}>
                  {plan.name}
                </MenuItem>
              )
            })}
            {
              // if we get a wrong value in the query param, set it as an option so that it gets displayed
              values.subscriptionPlanName !== NOTSET &&
                !subscriptionPlanData?.getSubscriptionPlans.find(
                  (p) => p.name === values.subscriptionPlanName,
                ) && (
                  <MenuItem
                    value={values.subscriptionPlanName}
                    key={values.subscriptionPlanName}
                  >
                    {values.subscriptionPlanName}
                  </MenuItem>
                )
            }
          </Select>
        </>
      )}
      {touched.subscriptionPlanName && errors.subscriptionPlanName && (
        <FormHelperText style={{ color: colors.red }}>
          {errors.subscriptionPlanName}
        </FormHelperText>
      )}
    </FormControl>
  )
}
